/**
 * The lesson page the Professor's Slack DM opens: /academy/lesson/:attemptId. One screen built from the lesson's blocks,
 * in the look of docs/academy-sample-lesson.html. Nothing is typed here; questions and pushback go in the Slack DM.
 */
import { useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { ApiError } from '@diggens/api-client';
import type { Lesson } from '@diggens/types';
import { LessonBlockView } from '../../components/academy/blocks';
import { useLesson, type LessonResponse } from '../../components/academy/useLesson';

/** "Core · Lesson 4 of 6", or just the position when the attempt is on no track. */
export function breadcrumb(track: string | null, position: LessonResponse['position']): string {
  const lesson = `Lesson ${position.index} of ${position.count}`;
  if (!track) return lesson;
  return `${track.charAt(0).toUpperCase()}${track.slice(1)} · ${lesson}`;
}

export function footerText(next: Lesson['next']): string {
  const dm = 'Reply in your Slack DM with any question for the Professor, or tell it where this is wrong.';
  if (!next) return dm;
  const tomorrow = /[.!?]$/.test(next) ? next : `${next}.`;
  return `${dm} Tomorrow: ${tomorrow}`;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) return 'This lesson is not one you can open, or it no longer exists.';
  if (error instanceof ApiError && error.status === 500) return 'This lesson could not be read. Tell the Professor in Slack and it will be looked at.';
  return 'The lesson could not be loaded. Try again in a moment.';
}

export function LessonView({ data }: { data: LessonResponse }) {
  const { lesson } = data;
  return (
    <Box sx={{ maxWidth: 1040, mx: 'auto', px: 2, pt: 4, pb: 8 }}>
      <Typography component="div" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 13 }}>
        {breadcrumb(data.track, data.position)}
      </Typography>
      <Typography component="h1" variant="h4" sx={{ fontWeight: 700, mt: 0.75, mb: 0.5 }}>
        {lesson.title}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {lesson.lede}
      </Typography>
      <Stack spacing={2}>
        {lesson.blocks.map((block, index) => (
          <LessonBlockView key={index} block={block} />
        ))}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
        {footerText(lesson.next)}
      </Typography>
    </Box>
  );
}

export function LessonPage() {
  const { attemptId = '' } = useParams<{ attemptId: string }>();
  const { data, isLoading, error } = useLesson(attemptId);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }
  if (error || !data) {
    return (
      <Box sx={{ maxWidth: 1040, mx: 'auto', px: 2, pt: 4 }}>
        <Alert severity={error instanceof ApiError && error.status === 404 ? 'info' : 'error'}>{errorMessage(error)}</Alert>
      </Box>
    );
  }
  return <LessonView data={data} />;
}
